import React, { useState, useEffect, useCallback } from "react";
import PageContainer from "./PageContainer";
import "../assets/transfer-portal.css";

import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import TimelineIcon from "@mui/icons-material/Timeline";
import UpdateIcon from "@mui/icons-material/Update";
import ClassIcon from "@mui/icons-material/Class";
import SortIcon from "@mui/icons-material/Sort";

import ContactPhoneIcon from "@mui/icons-material/ContactPhone";

import AssessmentIcon from "@mui/icons-material/Assessment";

import { Link } from "react-router-dom";
import {
  X_factor_getCollegeColor,
  hasLoggedEnoughMinutes,
  DEFAULT_XFACTOR_LABEL,
} from "../components/Utils/functions";
import axios from "axios";
import Loader from "../components/Utils/Loader";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import PortalFilterPop from "../components/PortalFilterPop";
import PortalPlayerModal from "../components/PortalPlayerModal";

// Add a helper function to check for duplicates by ID
const removeDuplicates = (existingPlayers, newPlayers) => {
  const existingIds = new Set(existingPlayers.map((player) => player.id));
  return newPlayers.filter((player) => !existingIds.has(player.id));
};

const NCAARatings = () => {
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    position: "all",
    teams: "all",
  });
  const [sortOption, setSortOption] = useState("xfactor");
  const [lastUpdated, setLastUpdated] = useState(
    new Date().toLocaleDateString()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilterTip, setShowFilterTip] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [totals, setTotals] = useState({
    committed: 0,
    available: 0,
  });
  const [openFilter, setOpenFilter] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  const positions = ["PG", "SG", "SF", "PF", "C"];

  const filterOptions = {
    position: [
      { value: "all", label: "All Positions" },
      ...positions.map((pos) => ({ value: pos, label: pos })),
    ],
    teams: [
      { value: "all", label: "All Teams" },
      // Teams options will be dynamically populated by the autocomplete
    ],
    sortOption: [
      // { value: "updated", label: "Recently Updated" },
      { value: "xfactor", label: "X-Factor" },
      { value: "ppg", label: "Points (PPG)" },
      { value: "rpg", label: "Rebounds (RPG)" },
      { value: "apg", label: "Assists (APG)" },
      { value: "name", label: "Name" },
      { value: "rank", label: "Rank" },
    ],
  };

  const handleFilterClick = (filterType) => {
    setOpenFilter(filterType);
  };

  const handleFilterClose = () => {
    setOpenFilter(null);
  };

  const handleFilterSave = (filterType, selectedValue) => {
    if (filterType === "sortOption") {
      setSortOption(selectedValue);
    } else {
      setFilters((prev) => ({
        ...prev,
        [filterType]: selectedValue,
      }));
    }
  };

  // Remove debounce effect and replace with key press handler
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      setDebouncedSearchTerm(searchTerm);
    }
  };

  // Function to fetch players from API
  const fetchPlayers = async (page = 1, limit = 50, shouldAppend = false) => {
    try {
      setLoadingMore(shouldAppend);
      if (!shouldAppend) setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);

      if (debouncedSearchTerm) {
        params.append("search", debouncedSearchTerm);
      }

      if (filters.position !== "all") {
        // For multiple positions, we pass them as a comma-separated list
        params.append("position", filters.position);
      }

      if (filters.teams !== "all") {
        params.append("teams", filters.teams);
      }

      // Always send sort parameter
      params.append("sort", sortOption);

      const response = await axios.get(
        `/api/portal/ncaa?${params.toString()}`,
        {
          withCredentials: true,
        }
      );

      const formattedData = response.data.players;
      setLastUpdated(new Date().toLocaleDateString());

      if (shouldAppend) {
        // Filter out any duplicate players before appending
        const uniqueNewPlayers = removeDuplicates(players, formattedData);

        // Only append if there are unique new players
        if (uniqueNewPlayers.length > 0) {
          setPlayers((prevPlayers) => [...prevPlayers, ...uniqueNewPlayers]);
          setFilteredPlayers((prevPlayers) => [
            ...prevPlayers,
            ...uniqueNewPlayers,
          ]);
        }
      } else {
        // Replace existing players
        setPlayers(formattedData);
        setFilteredPlayers(formattedData);
      }

      setPagination(response.data.pagination);

      // Set the totals from the response
      if (response.data.totals) {
        setTotals(response.data.totals);
      }
    } catch (err) {
      console.error("Error fetching players:", err);
      setError("Failed to load player data. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchPlayers(1, 50, false);
  }, []);

  // Fetch new data when filters or sort options change
  useEffect(() => {
    fetchPlayers(1, 50, false);
  }, [filters, sortOption, debouncedSearchTerm]);

  // Update isFiltered state whenever filters change
  useEffect(() => {
    const hasActiveFilters =
      filters.position !== "all" ||
      filters.teams !== "all" ||
      sortOption !== "xfactor" ||
      debouncedSearchTerm !== "";

    setIsFiltered(hasActiveFilters);
  }, [filters, sortOption, debouncedSearchTerm]);

  const loadMorePlayers = () => {
    if (pagination.page < pagination.pages) {
      fetchPlayers(pagination.page + 1, pagination.limit, true);
      setShowFilterTip(true);

      // Hide filter tip after 5 seconds
      setTimeout(() => {
        setShowFilterTip(false);
      }, 5000);
    }
  };

  const handleCardClick = (playerId, event) => {
    // Prevent the click event from propagating to parent elements
    event.stopPropagation();
    setSelectedPlayerId(playerId);
  };

  const handleCloseModal = () => {
    setSelectedPlayerId(null);
  };

  if (loading && !loadingMore) {
    return (
      <PageContainer>
        <div className="transfer-portal-loading">
          <Loader />
          <p>Loading player data...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="ncaa-ratings-container">
        <div className="ncaa-ratings-header">
          <div className="ncaa-ratings-title">
            <h1>NCAA Player Ratings</h1>
          </div>

          <div className="ncaa-ratings-last-updated">
            <UpdateIcon />
            <span>Last Updated: {lastUpdated}</span>
          </div>

          <div className="ncaa-ratings-search">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search players, colleges..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
            />
          </div>
        </div>

        <div className="ncaa-ratings-filters">
          <div className="transfer-portal-mobile-scroll-indicator">
            <span>Swipe</span>
            <ArrowForwardIcon fontSize="small" />
          </div>
          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("position")}
          >
            <label>
              <FilterListIcon />
              <span>Position</span>
            </label>
            <div className="filter-value">
              {filters.position === "all"
                ? "All Positions"
                : filters.position.includes(",")
                ? "Multiple"
                : filters.position}
            </div>
          </div>

          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("teams")}
          >
            <label>
              <SchoolIcon />
              <span>Teams</span>
            </label>
            <div className="filter-value">
              {filters.teams === "all"
                ? "All Teams"
                : filters.teams.includes(",")
                ? "Multiple"
                : filters.teams}
            </div>
          </div>

          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("sortOption")}
          >
            <label>
              <SortIcon />
              <span>Sort By</span>
            </label>
            <div className="filter-value">
              {sortOption === "updated"
                ? "Recently Updated"
                : sortOption === "xfactor"
                ? "X-Factor"
                : sortOption === "ppg"
                ? "Points (PPG)"
                : sortOption === "rpg"
                ? "Rebounds (RPG)"
                : sortOption === "apg"
                ? "Assists (APG)"
                : sortOption === "name"
                ? "Name"
                : "Rank"}
            </div>
          </div>
        </div>

        <PortalFilterPop
          open={!!openFilter}
          filterType={openFilter}
          onClose={handleFilterClose}
          onSave={handleFilterSave}
          selectedValue={
            openFilter === "sortOption"
              ? sortOption
              : openFilter
              ? filters[openFilter].toString()
              : ""
          }
          options={openFilter ? filterOptions[openFilter] : []}
          title={
            openFilter === "position"
              ? "Filter by Position"
              : openFilter === "teams"
              ? "Filter by Team"
              : openFilter === "sortOption"
              ? "Sort By"
              : ""
          }
        />

        {error && (
          <div className="transfer-portal-error">
            <p>{error}</p>
            <button onClick={() => fetchPlayers(1, 50, false)}>Retry</button>
          </div>
        )}

        <div className="transfer-portal-content">
          {filteredPlayers.length === 0 ? (
            <div className="no-results-box">
              <TimelineIcon className="empty-icon" />
              <h3>Widen your criteria</h3>
              <p>We have no results for the filters selected below.</p>
              <div className="active-filters">
                {debouncedSearchTerm && (
                  <div className="active-filter">
                    <SearchIcon fontSize="small" />
                    <span>Search: {debouncedSearchTerm}</span>
                  </div>
                )}
                {filters.position !== "all" && (
                  <div className="active-filter">
                    <FilterListIcon fontSize="small" />
                    <span>Position: {filters.position}</span>
                  </div>
                )}
                {filters.teams !== "all" && (
                  <div className="active-filter">
                    <SchoolIcon fontSize="small" />
                    <span>Team: {filters.teams}</span>
                  </div>
                )}
                {sortOption !== "xfactor" && (
                  <div className="active-filter">
                    <SortIcon fontSize="small" />
                    <span>
                      Sorted by:{" "}
                      {
                        filterOptions.sortOption.find(
                          (opt) => opt.value === sortOption
                        )?.label
                      }
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setDebouncedSearchTerm("");
                  setFilters({
                    position: "all",
                    teams: "all",
                  });
                  setSortOption("xfactor");
                  fetchPlayers(1, 50, false);
                }}
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              <div className="transfer-portal-grid">
                {filteredPlayers.map((player, index) => {
                  const notEnoughMinutes = !hasLoggedEnoughMinutes(
                    player.games,
                    player.minutes_per_game
                  );
                  return (
                    <div
                      key={player.id || index}
                      className="ncaa-ratings-card"
                      onClick={(e) => handleCardClick(player.id, e)}
                      style={{ "--animation-order": index % 50 }}
                    >
                      <div className="ncaa-ratings-card-header">
                        <div className="transfer-portal-player-info">
                          {player.image && (
                            <div
                              style={{ backgroundColor: "white" }}
                              className="transfer-portal-player-image"
                            >
                              <img
                                src={player.image}
                                alt={player.name}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  // Use one of the backup images randomly instead of placeholder
                                  const backupImages = [
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                                  ];
                                  const randomBackup =
                                    backupImages[
                                      Math.floor(
                                        Math.random() * backupImages.length
                                      )
                                    ];
                                  e.target.src = randomBackup;
                                }}
                              />
                            </div>
                          )}
                          <div className="transfer-portal-player-name">
                            <h3>{player.name}</h3>
                            <div className="transfer-portal-player-details-row">
                              <span className="transfer-portal-position">
                                {player.position}
                              </span>
                              <span className="transfer-portal-class">
                                <ClassIcon fontSize="small" />
                                {player.class}
                              </span>
                            </div>
                            <div className="ncaa-ratings-rank-row">
                              <span className="ncaa-ratings-rank-label">
                                Rank:
                              </span>
                              <span className="ncaa-ratings-rank-value">
                                {player.rank || (player.rank !== 0 && "-")}
                              </span>
                              {notEnoughMinutes && (
                                <div
                                  style={{ marginTop: "0" }}
                                  className="transfer-portal-minutes-note"
                                >
                                  * Didn't play much:{" "}
                                  {Math.round(
                                    player.games * player.minutes_per_game
                                  )}{" "}
                                  total mins logged
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="transfer-portal-xfactor">
                          <span>X-Factor</span>
                          <div
                            className="transfer-portal-xfactor-value"
                            style={{
                              color: notEnoughMinutes
                                ? "#666"
                                : X_factor_getCollegeColor(player.xfactor),
                            }}
                          >
                            {notEnoughMinutes
                              ? DEFAULT_XFACTOR_LABEL
                              : player.xfactor}
                          </div>
                        </div>
                      </div>

                      <div className="transfer-portal-card-body">
                        <div className="ncaa-ratings-school-info">
                          <div className="ncaa-ratings-school">
                            <SchoolIcon />
                            <span>{player.school}</span>
                          </div>
                          <div className="ncaa-ratings-conference">
                            <span className="ncaa-ratings-conference-label">
                              Conference:
                            </span>
                            <span className="ncaa-ratings-conference-value">
                              {player.conference || "ACC"}
                            </span>
                          </div>
                        </div>

                        <div className="ncaa-ratings-stats-row">
                          <div
                            className={`transfer-portal-stat-item ${
                              sortOption === "ppg" ? "sort-highlight" : ""
                            }`}
                          >
                            <span className="transfer-portal-stat-value">
                              {player.ppg.toFixed(1)}
                              {sortOption === "ppg" && (
                                <span className="sort-highlight-rank">
                                  #{index + 1}
                                </span>
                              )}
                            </span>
                            <span className="transfer-portal-stat-label">
                              PPG
                            </span>
                          </div>
                          <div
                            className={`transfer-portal-stat-item ${
                              sortOption === "rpg" ? "sort-highlight" : ""
                            }`}
                          >
                            <span className="transfer-portal-stat-value">
                              {player.rpg.toFixed(1)}
                              {sortOption === "rpg" && (
                                <span className="sort-highlight-rank">
                                  #{index + 1}
                                </span>
                              )}
                            </span>
                            <span className="transfer-portal-stat-label">
                              RPG
                            </span>
                          </div>
                          <div
                            className={`transfer-portal-stat-item ${
                              sortOption === "apg" ? "sort-highlight" : ""
                            }`}
                          >
                            <span className="transfer-portal-stat-value">
                              {player.apg.toFixed(1)}
                              {sortOption === "apg" && (
                                <span className="sort-highlight-rank">
                                  #{index + 1}
                                </span>
                              )}
                            </span>
                            <span className="transfer-portal-stat-label">
                              APG
                            </span>
                          </div>
                          <div className="transfer-portal-stat-item">
                            <span className="transfer-portal-stat-value">
                              {Math.round(player.xf_pct * 100)}%
                            </span>
                            <span className="transfer-portal-stat-label">
                              Percentile
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="transfer-portal-card-footer ncaa-ratings-card-footer">
                        <button
                          className="ncaa-ratings-view-details"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlayerId(player.id);
                          }}
                          style={{ animation: "none" }}
                        >
                          <AssessmentIcon fontSize="small" />
                          <span>Advanced Metrics</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {pagination.page < pagination.pages && (
                <div className="transfer-portal-load-more">
                  <button
                    className="ncaa-ratings-load-more-btn"
                    onClick={loadMorePlayers}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <span>Loading...</span>
                    ) : (
                      <>
                        <AddIcon />
                        <span>See More Players</span>
                      </>
                    )}
                  </button>
                  {showFilterTip && (
                    <div className="transfer-portal-filter-tip">
                      <Tooltip title="You can use the filters at the top to narrow down your search">
                        <InfoIcon className="transfer-portal-info-icon" />
                      </Tooltip>
                      <span>
                        Pro Tip: Use the filters above to narrow your search!
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Render the modal only when there's a selected player */}
        {selectedPlayerId && (
          <PortalPlayerModal
            playerId={selectedPlayerId}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </PageContainer>
  );
};

export default NCAARatings;
